'use client'
import { useEffect, useMemo, useState } from "react"
import { PickerProps, Translations } from "./DatePicker"
import { DatePreset } from './Preset'
import { Calendar as CalendarPrimitive } from "@/components/molecules/Calendar"
import * as PopoverPrimitives from "@radix-ui/react-popover"
import { enUS } from "date-fns/locale"
import { TimeValue } from "@react-aria/datepicker"
import { Time } from "@internationalized/date"
import { Trigger } from "./Trigger"
import { CalendarPopover } from "./CalendarPopover"
import { Button } from "@/components/atoms"
import { TimeInput } from "./TimeInput"
import { formatDate } from "./utils"
import { PresetContainer } from "./Preset"
import { cx } from "@/lib/utils"


export type SingleDatePickerProps = {
    presets?: DatePreset[]
    defaultValue?: Date
    value?: Date
    onChange?: (date: Date | undefined) => void
} & PickerProps

export interface SingleProps extends Omit<PickerProps, "translations"> {
    presets?: DatePreset[]
    defaultValue?: Date
    value?: Date
    onChange?: (date: Date | undefined) => void
    translations?: Omit<Translations, "range">
}

const SingleDatePicker = ({
    defaultValue,
    value,
    onChange,
    presets,
    disabled,
    disabledDays,
    disableNavigation,
    className,
    showTimePicker,
    placeholder = "Select date",
    hasError,
    translations,
    enableYearNavigation = false,
    locale = enUS,
    align = "center",
    ...props
}: SingleProps) => {
    const [open, setOpen] = useState(false)
    const [date, setDate] = useState<Date | undefined>(
        value ?? defaultValue ?? undefined,
    )
    const [month, setMonth] = useState<Date | undefined>(date)

    const [time, setTime] = useState<TimeValue | null>(
        value
            ? new Time(value.getHours(), value.getMinutes())
            : defaultValue
                ? new Time(defaultValue.getHours(), defaultValue.getMinutes())
                : new Time(0, 0),
    )

    const initialDate = useMemo(() => date, [open])

    useEffect(() => {
        setDate(value ?? defaultValue ?? undefined)
    }, [value, defaultValue])

    useEffect(() => {
        if (date) {
            setMonth(date)
        }
    }, [date])

    useEffect(() => {
        if (!open) {
            setMonth(date)
        }
    }, [open])

    const onCancel = () => {
        setDate(initialDate)
        setTime(
            initialDate
                ? new Time(initialDate.getHours(), initialDate.getMinutes())
                : new Time(0, 0),
        )
        setOpen(false)
    }

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onCancel()
        }

        setOpen(open)
    }

    const onDateChange = (date: Date | undefined) => {
        const newDate = date
        if (showTimePicker) {
            if (newDate && !time) {
                setTime(new Time(0, 0))
            }
            if (newDate && time) {
                newDate.setHours(time.hour)
                newDate.setMinutes(time.minute)
            }
        }
        setDate(newDate)
    }

    const onTimeChange = (time: TimeValue | null) => {
        setTime(time)

        if (!date) {
            return
        }

        const newDate = new Date(date.getTime())

        if (!time) {
            newDate.setHours(0)
            newDate.setMinutes(0)
        } else {
            newDate.setHours(time.hour)
            newDate.setMinutes(time.minute)
        }

        setDate(newDate)
    }

    const formattedDate = useMemo(() => {
        if (!date) {
            return null
        }

        return formatDate(date, locale, showTimePicker)
    }, [date, locale, showTimePicker])

    const onApply = () => {
        setOpen(false)
        onChange?.(date)
    }

    useEffect(() => {
        setDate(value ?? defaultValue ?? undefined)
        setTime(
            value
                ? new Time(value.getHours(), value.getMinutes())
                : defaultValue
                    ? new Time(defaultValue.getHours(), defaultValue.getMinutes())
                    : new Time(0, 0),
        )
    }, [value, defaultValue])

    return (
        <PopoverPrimitives.Root
            tremor-id="tremor-raw"
            open={open}
            onOpenChange={onOpenChange}
        >
            <Trigger
                placeholder={placeholder}
                disabled={disabled}
                className={className}
                hasError={hasError}
                aria-required={props.required || props["aria-required"]}
                aria-invalid={props["aria-invalid"]}
                aria-label={props["aria-label"]}
                aria-labelledby={props["aria-labelledby"]}
            >
                {formattedDate}
            </Trigger>
            <CalendarPopover align={align}>
                <div className="flex">
                    <div className="flex flex-col sm:flex-row sm:items-start">
                        {presets && presets.length > 0 && (
                            <div
                                className={cx(
                                    "relative flex h-14 w-full items-center sm:h-full sm:w-40",
                                    "border-b border-gray-200 sm:border-r sm:border-b-0",
                                    "overflow-auto",
                                )}
                            >
                                <div className="absolute px-2 pr-2 sm:inset-0 sm:left-0 sm:py-2">
                                    <PresetContainer
                                        currentValue={date}
                                        presets={presets}
                                        onSelect={onDateChange}
                                    />
                                </div>
                            </div>
                        )}
                        <div>
                            <CalendarPrimitive
                                mode="single"
                                month={month}
                                onMonthChange={setMonth}
                                selected={date}
                                onSelect={onDateChange}
                                disabled={disabledDays}
                                locale={locale}
                                enableYearNavigation={enableYearNavigation}
                                disableNavigation={disableNavigation}
                                initialFocus
                                {...props}
                            />
                            {showTimePicker && (
                                <div className="border-t border-gray-200 p-3">
                                    <TimeInput
                                        aria-label="Time"
                                        onChange={onTimeChange}
                                        isDisabled={!date}
                                        value={time}
                                        isRequired={props.required}
                                    />
                                </div>
                            )}
                            <div className="flex items-center gap-x-2 border-t border-gray-200 p-3">
                                <Button
                                    variant="light"
                                    className="h-8 w-full"
                                    type="button"
                                    onClick={onCancel}
                                >
                                    {translations?.cancel ?? "Cancel"}
                                </Button>
                                <Button
                                    variant="primary"
                                    className="h-8 w-full"
                                    type="button"
                                    onClick={onApply}
                                >
                                    {translations?.apply ?? "Apply"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </CalendarPopover>
        </PopoverPrimitives.Root>
    )
}


SingleDatePicker.displayName = "SingleDatePicker"

export { SingleDatePicker }